/**
 * LedgerEditModal — amount / date / narration edit for legacy ledger rows
 * (order_payment · bill_payment · voucher · opening_settlement). Settlement
 * rows use SettlementEditor instead (bill-wise re-allocation).
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Pencil, Save } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function LedgerEditModal({ entry, clientId, onClose, onSaved }) {
  const amount = entry.debit > 0 ? entry.debit : entry.credit;
  const [form, setForm] = useState({
    amount: String(amount || ''),
    date: (entry.date || '').slice(0, 10),
    narration: entry.narration || '',
  });
  const [busy, setBusy] = useState(false);

  const submit = async (ev) => {
    ev.preventDefault();
    const newAmt = parseFloat(form.amount);
    if (!(newAmt > 0)) { toast.error('Amount must be > 0'); return; }
    setBusy(true);
    try {
      await axios.patch(`${API}/clients/${clientId}/ledger-entry`, {
        source_kind: entry.source_kind, source_id: entry.source_id, sub_id: entry.sub_id || '',
        old_amount: amount, old_date: (entry.date || '').slice(0, 10),
        amount: newAmt, date: form.date, narration: form.narration,
      }, { withCredentials: true });
      toast.success('Ledger entry updated · cashflow + source synced');
      await onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Edit failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-end sm:items-center justify-center z-[70] p-0 sm:p-4" data-testid="ledger-edit-modal" onClick={onClose}>
      <div className="bg-zinc-900 border border-amber-700/50 rounded-t-2xl sm:rounded-2xl p-5 w-full sm:max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-zinc-50 font-semibold text-sm flex items-center gap-2"><Pencil size={14} className="text-amber-400" /> Edit Ledger Entry</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X size={16} /></button>
        </div>
        <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-2.5 mb-3 text-[11px]">
          <div className="text-zinc-500 mb-0.5">{entry.type} · <span className="font-mono text-amber-400/80">{entry.reference}</span></div>
          <div className="text-zinc-400 truncate">{entry.description}</div>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-zinc-400 text-xs mb-1 block">Amount (₹) *</label>
              <input type="number" required step="0.01" min="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} data-testid="ledger-edit-amount"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
            </div>
            <div>
              <label className="text-zinc-400 text-xs mb-1 block">Date *</label>
              <input type="date" required value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} data-testid="ledger-edit-date"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-zinc-400 text-xs mb-1 block">Narration</label>
            <input value={form.narration} onChange={e => setForm(f => ({ ...f, narration: e.target.value }))} data-testid="ledger-edit-narration" placeholder="Optional note for audit trail"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-xs" />
          </div>
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2 rounded-lg border border-zinc-700 text-zinc-400 text-xs">Cancel</button>
            <button type="submit" disabled={busy} data-testid="ledger-edit-save"
              className="flex-1 py-2 rounded-lg bg-amber-500 text-black font-semibold text-xs disabled:opacity-50 flex items-center justify-center gap-1.5">
              <Save size={11} /> {busy ? 'Saving…' : 'Save & Cascade'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
